import {Modal} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Image} from 'src/design/atoms/Image';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {produce} from 'immer';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {DeleteButton} from 'src/design/molecules/DeleteButton';
import {ContentTitle} from 'src/design/atoms/Typography';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {useAppSelector} from 'src/redux/hooks';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

export const ReferenceImageSettings: React.FC = () => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();
  const [modalOpen, setModalOpen] = useState(false);
  const {accessToken} = useAppSelector(state => state.auth);

  const handleImageUpload = React.useCallback(
    (name: string, value: any) => {
      if (!field) return;

      // Parse the uploaded file data
      const uploadedFiles = JSON.parse(value);
      if (uploadedFiles && uploadedFiles.length > 0) {
        const file = uploadedFiles[0];

        // Extract the key from the URL (assuming the key is the last part of the URL)
        const urlParts = file.value.split('/');
        const key = urlParts[urlParts.length - 1];

        updateSelectedField(
          produce(field, draft => {
            if (!draft.builderOptions) {
              draft.builderOptions = {};
            }
            draft.builderOptions.referenceImageUrl = file.value;
            draft.builderOptions.referenceImageKey = key;
          })
        );
      }
    },
    [field, updateSelectedField]
  );

  const handleRemoveImage = React.useCallback(() => {
    if (!field) return;

    updateSelectedField(
      produce(field, draft => {
        if (!draft.builderOptions) {
          draft.builderOptions = {};
        }
        draft.builderOptions.referenceImageUrl = undefined;
        draft.builderOptions.referenceImageKey = undefined;
      })
    );
  }, [field, updateSelectedField]);

  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setModalOpen(false);
  }, []);

  if (!field) return null;

  const hasReferenceImage = !!field.builderOptions?.referenceImageUrl;

  return (
    <>
      <ContentTitle text="Reference Image" />

      <Stack gap="sm">
        {hasReferenceImage ? (
          <>
            <Text size="sm" c="dimmed">
              Reference Image Uploaded!
            </Text>
            <Group>
              <Button
                size="sm"
                color="black"
                variant="outline"
                onClick={handleOpenModal}>
                Change Image
              </Button>
              <DeleteButton
                c="red"
                size="sm"
                variant="outline"
                onClick={handleRemoveImage}>
                Remove Image
              </DeleteButton>
            </Group>
          </>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              No reference image attached to this field.
            </Text>
            <Button
              size="sm"
              color="black"
              variant="outline"
              w="fit-content"
              onClick={handleOpenModal}>
              Add Reference Image
            </Button>
          </>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={handleCloseModal}
        title={<Title order={4}>Reference Image for: {field.label}</Title>}
        size="xl">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Upload a reference image that will be associated with this field.
            This image can be used to provide visual guidance or examples for
            users.
          </Text>
          <Text size="sm" c="dimmed">
            Recommend image size is <b>1200px width by 700px height</b> for best
            results. Image will be adjusted to fit when it is rendered.
          </Text>
          <Text size="sm" c="dimmed">
            Image scaled to 350px for preview below.
          </Text>

          <Text size="sm">Current Reference Image</Text>
          {field.builderOptions?.referenceImageUrl ? (
            <Image
              src={getUrlWithEncodingAndAuth(
                field.builderOptions?.referenceImageUrl,
                accessToken
              )}
              alt="Reference image"
              h={350}
              w={350}
              fit="contain"
              radius="sm"
            />
          ) : (
            <Text size="sm" c="dimmed">
              No reference image uploaded yet.
            </Text>
          )}

          <FormFileUploadV2
            field={{
              name: 'referenceImage',
              label: 'Upload Reference Image',
              type: 'file',
              fileUploadOptions: {
                allowedFileTypes: [
                  'image/jpeg',
                  'image/png',
                  'image/gif',
                  'image/webp',
                ],
                allowMultiple: false,
                maxFileSize: 10 * 1024 * 1024, // 10MB
              },
            }}
            value="[]"
            onChange={handleImageUpload}
          />

          <Group justify="flex-end" mt="md">
            <Button
              onClick={handleCloseModal}
              variant="subtle"
              color="black">
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
